import * as Yup from 'yup';
import { parseFieldMarkup } from '../utils/TextProcessing';
import { isBoolean } from 'lodash';
import { serverTimestamp } from 'firebase/firestore';
import moment from 'moment/moment';
const urlErrorMessage = 'Urls must begin with http:// or https://';

const todayDate = ({
  includeTime = true,
  includeMilliseconds = true,
  includeAmPm = false,
  dateSeparator = '/',
  timeSeparator = ':',
  spaceSeparator = ' '
}) => {
  const today = new Date();
  let dd = today.getDate();
  let mm = today.getMonth() + 1;
  let hour = today.getHours();
  let minute = today.getMinutes();
  const ms = today.getMilliseconds();

  const yyyy = today.getFullYear();
  if (dd < 10) {
    dd = '0' + dd;
  }
  if (mm < 10) {
    mm = '0' + mm;
  }
  if (hour < 10) {
    hour = '0' + hour;
  }
  if (minute < 10) {
    minute = '0' + minute;
  }

  const amPm = today.getHours() < 13 ? 'AM' : 'PM';

  let result = `${dd}${dateSeparator}${mm}${dateSeparator}${yyyy}`;

  if (includeTime) {
    result = `${result}${spaceSeparator}${hour}${timeSeparator}${minute}`;
  }
  if (includeMilliseconds) {
    result = `${result}-${ms}`;
  }
  if (includeAmPm) {
    result = `${result} ${amPm}`;
  }
  return result;
};
const getFieldValidation = field => {
  if (field.type === 'object') {
    const nestedYup = {};
    field.fields.forEach(nestedField => {
      const v = getFieldValidation(nestedField);
      if (v) {
        nestedYup[nestedField.name] = v;
      }
    });
    return Object.keys(nestedYup).length > 0
      ? Yup.object().shape(nestedYup)
      : undefined;
  } else if (!field.validation) {
    return;
  }

  const requiredValidator = field.validation.find(v => v.type === 'required');
  const emailValidator = field.validation.find(v => v.type === 'email');
  const urlValidator = field.validation.find(v => v.type === 'url');
  const maxValidator = field.validation.find(v => v.type === 'max');

  if (requiredValidator && emailValidator) {
    return Yup.string()
      .required(requiredValidator.errorMessage)
      .email(emailValidator.errorMessage);
  } else if (requiredValidator && urlValidator) {
    return Yup.string()
      .required(requiredValidator.errorMessage)
      .url(urlValidator.errorMessage || urlErrorMessage);
  } else if (requiredValidator && maxValidator) {
    return Yup.string()
      .required(requiredValidator.errorMessage)
      .max(maxValidator.maxLength, maxValidator.errorMessage);
  } else if (urlValidator) {
    return Yup.string().url(urlValidator.errorMessage || urlErrorMessage);
  } else if (maxValidator) {
    return Yup.string().max(maxValidator.maxLength, maxValidator.errorMessage);
  } else if (emailValidator) {
    return Yup.string().email(emailValidator.errorMessage);
  } else if (requiredValidator) {
    return Yup.string().required(requiredValidator.errorMessage);
  }
};

const validationSchema = (contentType, filterFields = []) => {
  const yup = {};

  contentType.fields
    .filter(f =>
      filterFields.length > 0
        ? filterFields.some(field => field === f.name)
        : true
    )
    .forEach(field => {
      const validation = getFieldValidation(field);
      if (validation) {
        yup[field.name] = validation;
      }
    });

  return Yup.object().shape(yup);

  /*return Yup.object({
    title: Yup.string().required('title is required'),
    home: Yup.object({
      companyName: Yup.string().required('Company name is required')
    })
  });*/
};

const generateUniqueCode = ({ length = 12 }) => {
  const characters = '023456789ABCDEFGHJKLMNOPQRSTUVWXYZ';
  let code = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    code += characters[randomIndex];
  }
  return code;
};

const getFieldCollectionDefaults = (
  fields,
  existingItemCount,
  currentDocument,
  user
) => {
  const data = {};

  function replaceUniqueCodes(inputString) {
    return inputString.replace(/\{unique_code_(\d+)\}/g, (match, length) => {
      return generateUniqueCode({ length: parseInt(length, 10) });
    });
  }

  fields
    //.filter(item => !item.hidden && !item.readonly)
    .forEach(item => {
      if (
        item?.defaultValue?.indexOf &&
        item?.defaultValue?.indexOf('{unique_code') > -1
      ) {
        data[item.name] = replaceUniqueCodes(item?.defaultValue);
        /*let length = 12;
        const match = item.defaultValue.match(/\{unique_code_(\d+)\}/);
        if (match && match.length > 1) {
          length = parseInt(match[1], 10);
          data[item.name] = generateUniqueCode({ length });
        } else {
          data[item.name] = generateUniqueCode({ length });
        }*/
      } else if (item.type === 'image') {
        data[item.name] = []; // images are stored in arrays
      } else if (item?.defaultValue === 'day') {
        data[item.name] = moment().day();
      } else if (item?.defaultValue === 'dayname') {
        data[item.name] = moment().format('dddd');
      } else if (item?.defaultValue === 'week') {
        data[item.name] = moment().week();
      } else if (item?.defaultValue === 'month') {
        data[item.name] = moment().month();
      } else if (item?.defaultValue === 'monthname') {
        data[item.name] = moment().format('MMMM');
      } else if (item?.defaultValue === 'year') {
        data[item.name] = moment().year();
      } else if (item.autogenerate && item.autogenerate.enabled) {
        data[item.name] = generateAutoId(
          item,
          existingItemCount,
          currentDocument
        );
      } else if (item.type === 'number') {
        data[item.name] = item.defaultValue || 0;
      } else if (item.type === 'boolean' || isBoolean(item.defaultValue)) {
        if (
          item.defaultValue === 'false' ||
          item.defaultValue === false ||
          item.defaultValue === undefined
        ) {
          data[item.name] = false;
        } else if (item.defaultValue === 'true' || item.defaultValue === true) {
          data[item.name] = true;
        } else {
          data[item.name] = item.defaultValue;
        }
      } else if (item.defaultValue === '') {
        data[item.name] = '';
      } else if (!item.defaultValue) {
        // na
      } else if (
        item.defaultValue === 'user.email' ||
        item.defaultValue === 'user'
      ) {
        data[item.name] = user.email;
      } else if (
        !isBoolean(item.defaultValue) &&
        !item.disableParsing &&
        item.defaultValue.indexOf &&
        item.defaultValue.indexOf('${source.user.') > -1
      ) {
        data[item.name] = parseFieldMarkup({
          record: user,
          returnAsString: true,
          fieldMarkup: item.defaultValue,
          fieldPrefix: 'user'
        });
      } else if (item.name?.toLowerCase() === 'id') {
        //data[item.name] = Date.now();
      } else if (item?.name === 'date' || item?.defaultValue === 'date') {
        data[item.name] = serverTimestamp();
      } else if (
        item?.name === 'datetime' ||
        item?.defaultValue === 'datetime'
      ) {
        data[item.name] = serverTimestamp();
        //data[item.name] = todayDate({ includeTime: true });
      } else if (item.defaultValue === 'count') {
        data[item.name] = existingItemCount + 1;
      } else if (item.type === 'object') {
        data[item.name] = getFieldCollectionDefaults(
          item.fields,
          existingItemCount,
          currentDocument
        );
      } else if (item.type === 'font') {
        data[`${item.name}FontSize`] = item.default || 14;
        data[`${item.name}TextDecoration`] = 'none';
        data[`${item.name}FontStyle`] = 'normal';
        data[`${item.name}FontWeight`] = 'normal';
      } else if (
        item.defaultValue &&
        !item.disableParsing &&
        item.defaultValue.indexOf &&
        item.defaultValue.indexOf('${') > -1
      ) {
        data[item.name] = parseFieldMarkup({
          record: currentDocument,
          returnAsString: true,
          fieldMarkup: item.defaultValue
        });
      } else {
        data[item.name] = item.defaultValue || '';
      }
    });

  return data;
};

/*
  Generates an ID, i.e. like an account number
  These are set on the content type, against a field, in this format
  {
      name: 'accountNumber',
      label: 'Account #',
      autogenerate: {
        enabled: true,
        prefix: 'CL',
        suffix: '',
        paddingChar: '0',
        maxLength: 10,
        startFrom: 1,
        useDate: false
      },
      type: 'string',
      defaultValue: '',
      description: 'The account number of the client',
      editable: false,
      hidden: false,
      readonly: true,
      help: {
        text: '',
        videoUri: '',
        videoRelatedLinks: []
      },
      validation: []
    },
*/
const generateAutoId = (field, existingItemCount, currentDocument) => {
  if (!field.autogenerate || !field.autogenerate.enabled) {
    return field.defaultValue || '';
  }

  const {
    prefix,
    suffix,
    paddingChar,
    maxLength,
    startFrom,
    useDate,
    includeTime,
    includeMilliseconds,
    includeAmPm,
    dateSeparator,
    timeSeparator,
    spaceSeparator
  } = field.autogenerate;

  if (useDate) {
    return `${prefix}${todayDate({
      includeTime,
      includeMilliseconds,
      includeAmPm,
      dateSeparator,
      timeSeparator,
      spaceSeparator
    })}${suffix}`;
  }

  let newPrefix = parseFieldMarkup({
    record: currentDocument,
    fieldDataSource: '',
    fieldMarkup: prefix,
    returnAsString: true
  });

  if (!newPrefix || newPrefix?.trim().length === '') {
    newPrefix = prefix;
  }
  return `${newPrefix}${paddingChar.repeat(
    maxLength - (startFrom + existingItemCount + 1).toString().length
  )}${startFrom + existingItemCount + 1}${suffix}`;
};

const defaultDoc = (contentType, existingItemCount, currentDocument, user) => {
  const defaultData = getFieldCollectionDefaults(
    contentType.fields,
    existingItemCount || 0,
    currentDocument,
    user
  );

  defaultData.deleted = false;
  defaultData.sortOrder = 0;

  if (!defaultData.id) {
    defaultData.id = `id_${Date.now()}`;
  }

  return defaultData;
};

/*
  parse a path such as
  assessments/
  assessments/:assessmentId
  assessments/:assessmentId/equipment/:equipmentId
*/
const parseFirestorePath = ({
  id,
  route,
  params,
  contentType,
  parentType,
  organisationId
}) => {
  if (!route) {
    return '';
  }

  let rel = route?.relationship;

  if (contentType && parentType && !contentType?.hasOwnCollection && !rel) {
    // We have no relationship passed in. This will be because we have a type that we have chosen to show in the sidebar
    // but it is actually a child type and we need to find the parent relationship
    rel = parentType.relationships.find(
      r => r.contentTypeName === contentType.type && r.type === 'child'
    );
  }

  let path =
    rel?.useCustomFirestorePath && rel?.customFirestorePath?.trim().length > 0
      ? rel.customFirestorePath
      : route.path;

  if (path === undefined) {
    if (contentType && contentType?.hasOwnCollection) {
      path = contentType.collectionName;
    } else {
      path = route.url.substring(1).replace('-', '_');
    }
  }

  if (
    rel?.useUserOrganisationId &&
    path.indexOf(':organisationId') > -1 &&
    organisationId
  ) {
    path = path.replace(':organisationId', organisationId);
    path = path.replace('{organisationId}', organisationId);
  }

  if (!params || Object.keys(params).length === 0) {
    if (path.indexOf(':') > -1 && id) {
      return `${path}/${id}`;
    } else {
      return path;
    }
  }

  Object.keys(params).forEach(paramName => {
    const value = params[paramName];
    const regexColon = new RegExp(`:${paramName}`, 'g');
    const regexBraces = new RegExp(`{${paramName}}`, 'g');

    path = path.replace(regexColon, value);
    path = path.replace(regexBraces, value);
  });

  if (path.indexOf(':') > -1 && id) {
    return `${path}/${id}`;
  }

  return path;
};

export { defaultDoc, validationSchema, parseFirestorePath, generateAutoId };
